"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Plus, Send } from "lucide-react";

interface StepTeamProps {
  brandId: string;
  onContinue: () => void;
  onSkip: () => void;
  onBack: () => void;
}

export function StepTeam({ brandId, onContinue, onSkip, onBack }: StepTeamProps) {
  const { t } = useTranslation("wizard");
  const [emails, setEmails] = useState<string[]>([""]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  function addEmail() { setEmails((prev) => [...prev, ""]); }
  function updateEmail(i: number, val: string) { setEmails((prev) => prev.map((e, idx) => idx === i ? val : e)); }
  function removeEmail(i: number) { setEmails((prev) => prev.filter((_, idx) => idx !== i)); }

  const validEmails = emails.filter((e) => e.trim().includes("@"));

  async function handleSend() {
    if (validEmails.length === 0) { onContinue(); return; }
    setSending(true);
    try {
      await fetch(`/api/brands/${brandId}/invites`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails: validEmails }),
      });
      setSent(true);
      setTimeout(onContinue, 1000);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-[18px] font-bold text-foreground tracking-tight">{t("steps.team.title")}</h2>
        <p className="text-[13px] text-muted-foreground mt-1">
          {t("steps.team.description")}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label>{t("steps.team.emailsLabel")}</Label>
        {emails.map((email, i) => (
          <div key={i} className="flex gap-2">
            <Input
              type="email"
              placeholder={t("steps.team.emailPlaceholder")}
              value={email}
              onChange={(e) => updateEmail(i, e.target.value)}
              className="flex-1"
            />
            {emails.length > 1 && (
              <button type="button" onClick={() => removeEmail(i)} className="text-muted-foreground hover:text-foreground transition-colors">
                <X size={14} />
              </button>
            )}
          </div>
        ))}
        <button type="button" onClick={addEmail} className="flex items-center gap-1 text-[12px] text-brand hover:opacity-80 transition-opacity w-fit">
          <Plus size={12} /> {t("steps.team.addAnother")}
        </button>
      </div>

      <div className="flex items-center gap-2 mt-1">
        <Button variant="secondary" onClick={onBack}>{t("nav.back")}</Button>
        <Button className="flex-1 gap-1.5" onClick={handleSend} disabled={sending || sent}>
          {sent ? t("steps.team.invitesSent") : sending ? t("steps.team.sending") : <><Send size={13} /> {t("steps.team.sendInvites")}</>}
        </Button>
        <Button variant="ghost" className="text-muted-foreground" onClick={onSkip}>{t("nav.skip")}</Button>
      </div>
    </div>
  );
}
